/* PulseSpotlightRails — two athlete tiers at the top of /pulse (iter237).
 *
 *  1. "Top Athletes" — auto-ranked by social followers + profile views.
 *  2. "Athletes to Look Out For" — admin-curated (Admin → Pulse Spotlight).
 *
 * Both filter by sport through one shared chip row. Cards: photo, name,
 * sport · school, follow button.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Crown, Telescope } from 'lucide-react';
import VerifiedCheck from '../VerifiedCheck';
import FollowButton from '../FollowButton';
import { resolveImageUrl } from '../ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => {
  if (!n) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return String(n);
};

const SpotCard = ({ a, rank }) => (
  <div
    className="relative flex-shrink-0 w-[164px] sm:w-[180px] snap-start rounded-2xl bg-white/[0.03] ring-1 ring-white/10 hover:ring-white/25 transition overflow-hidden"
    data-testid={`spotlight-card-${a.slug}`}
  >
    <Link to={`/${a.slug}`} className="block">
      <div className="relative aspect-[4/5] bg-white/5">
        {a.photo ? (
          <img src={resolveImageUrl(a.photo, 400)} alt={a.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center font-display text-3xl text-white/25">
            {(a.name || '?').split(' ').map((s) => s[0]).slice(0, 2).join('')}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        {rank && (
          <span className="absolute top-2 left-2 inline-flex items-center justify-center min-w-[26px] h-[26px] px-1.5 rounded-full bg-black/70 backdrop-blur ring-1 ring-white/20 text-[11px] font-black text-amber-300">
            #{rank}
          </span>
        )}
        {(a.followers > 0 || a.profile_views > 0) && (
          <span className="absolute bottom-2 left-2 text-[9px] uppercase tracking-widest font-bold text-white/80">
            {a.followers > 0 ? `${fmt(a.followers)} followers` : `${fmt(a.profile_views)} views`}
          </span>
        )}
      </div>
    </Link>
    <div className="p-2.5">
      <Link to={`/${a.slug}`} className="flex items-center gap-1 min-w-0">
        <p className="text-sm font-bold text-white truncate">{a.name}</p>
        {a.verified && <VerifiedCheck size={13} />}
      </Link>
      <p className="text-[10px] uppercase tracking-widest text-white/45 truncate mt-0.5">
        {[a.sport, a.school].filter(Boolean).join(' · ') || '—'}
      </p>
      <FollowButton athleteId={a.id} athleteName={a.name} className="mt-2 w-full justify-center !px-2" />
    </div>
  </div>
);

const Rail = ({ icon: Icon, iconClass, kicker, title, athletes, ranked = false, testid }) => (
  <section className="mb-6" data-testid={testid}>
    <div className="flex items-center gap-2 mb-3">
      <Icon className={`w-4 h-4 ${iconClass}`} />
      <div>
        <p className={`text-[9px] uppercase tracking-[0.3em] font-black ${iconClass}`}>{kicker}</p>
        <h2 className="font-display text-xl sm:text-2xl uppercase tracking-tight leading-none text-white">{title}</h2>
      </div>
    </div>
    <div className="flex gap-3 overflow-x-auto no-scrollbar snap-x pb-1 -mx-4 px-4 sm:mx-0 sm:px-0">
      {athletes.map((a, i) => (
        <SpotCard key={a.id || a.slug} a={a} rank={ranked ? i + 1 : null} />
      ))}
    </div>
  </section>
);

export default function PulseSpotlightRails() {
  const [sport, setSport] = useState('');
  const [sports, setSports] = useState([]);
  const [top, setTop] = useState([]);
  const [watch, setWatch] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancel = false;
    const params = sport ? { sport } : {};
    Promise.all([
      axios.get(`${API}/pulse/top-athletes`, { params: { ...params, limit: 12 } }).then((r) => r.data).catch(() => null),
      axios.get(`${API}/pulse/watchlist`, { params }).then((r) => r.data).catch(() => null),
    ]).then(([t, w]) => {
      if (cancel) return;
      setTop(t?.athletes || []);
      setWatch(w?.athletes || []);
      const union = new Set([...(t?.sports || []), ...(w?.sports || [])]);
      setSports(Array.from(union).sort());
      setLoaded(true);
    });
    return () => { cancel = true; };
  }, [sport]);

  if (loaded && top.length === 0 && watch.length === 0 && !sport) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6" data-testid="pulse-spotlight">
      {/* Shared sport filter */}
      {sports.length > 1 && (
        <div className="flex gap-2 overflow-x-auto no-scrollbar mb-4 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="spotlight-sport-filters">
          <button
            type="button"
            onClick={() => setSport('')}
            className={`flex-shrink-0 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold ring-1 transition ${
              !sport ? 'bg-white text-black ring-white' : 'bg-white/5 text-white/60 ring-white/10 hover:bg-white/10'
            }`}
            data-testid="spotlight-sport-all"
          >
            All sports
          </button>
          {sports.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSport(s === sport ? '' : s)}
              className={`flex-shrink-0 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold ring-1 transition ${
                sport === s ? 'bg-white text-black ring-white' : 'bg-white/5 text-white/60 ring-white/10 hover:bg-white/10'
              }`}
              data-testid={`spotlight-sport-${s.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {top.length > 0 && (
        <Rail
          icon={Crown}
          iconClass="text-amber-300"
          kicker="Ranked by followers + views"
          title="Top Athletes"
          athletes={top}
          ranked
          testid="spotlight-top-athletes"
        />
      )}

      {watch.length > 0 && (
        <Rail
          icon={Telescope}
          iconClass="text-cyan-300"
          kicker="Curated by FLYT"
          title="Athletes to Look Out For"
          athletes={watch}
          testid="spotlight-watchlist"
        />
      )}
    </div>
  );
}
